import { useQuery } from '@apollo/client';
import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import * as styles from './ControlBar.module.scss';
import { POST_ACTIVITIES_END_OF_TIME } from './ControlBarActivity';
import ControlBarAddOrSharePopOver from './ControlBarAddOrSharePopOver';
import ControlBarBacklinks from './ControlBarBacklinks';
import ControlBarButton from './ControlBarButton';
import ControlBarInsightsButton from './ControlBarInsightsButton';
import {
  isActivityMenuOpenAtom,
  isAddOrShareMenuOpenAtom,
  isBacklinksMenuOpenAtom,
  isInsightsMenuOpenAtom,
} from './controlBarMenuAtoms';
import ControlBarPopOver from './ControlBarPopOver';
import ControlBarSeriesButton from './ControlBarSeriesButton';
import ControlBarVerificationButton from './ControlBarVerificationButton';
import SVGIcon from '../../components/SVGIcon';
import Timestamp from '../../components/Timestamp';
import { BEACON_POST_ADD_OR_SHARE } from '../../constants/onboarding';
import { useQuillReadOnly } from '../../editor/QuillContext';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated/gql';
import addOrShareIcon from '../../icons/non-prefilled/16x16/addOrShare.svg';
import backlinksIcon from '../../icons/non-prefilled/16x16/backlinks.svg';
import TutorialBeacon from '../../onboarding/tutorial/TutorialBeacon';
import getVerificationStatus, {
  VerificationStatus,
} from '../getVerificationStatus';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';
import VerifiedIcon from '../VerifiedIcon';
import ControlBarActivityButton from './ControlBarActivityButton';

const CONTROL_BAR_POST_QUERY = gql(/** @complexity 9 */ `
    query ControlBarPost($postId: ID!, $beforeAt: Datetime!) {
      post(id: $postId) {
        id
        ...ControlBarBacklinks_post
      }
      alphaPostActivities(postId: $postId, beforeAt: $beforeAt) {
        ...ControlBarActivityButton_activity
      }
    }
`);

const POST_FRAGMENT = gql(`
  fragment ControlBar_post on Post {
    id
    externalPost {
      id
    }
    updatedAt
    verificationEntries {
      id
      verifiedAt
    }
    ...ControlBarInsightsButton_post
    ...ControlBarAddOrSharePopOver_post
    ...ControlBarVerificationButton_post
    ...ControlBarActivityButton_post
    ...getVerificationStatus_post
  }
`);

const FOOTER_CONTROL_BAR_POST_FRAGMENT = gql(`
  fragment FooterControlBar_postDetails on Post {
    id
    archivedAt
    publishedAt
    ...ControlBarInsightsButton_post
  }
`);

const FOOTER_CONTROL_BAR_SERIES_FRAGMENT = gql(`
  fragment FooterControlBar_series on SeriesFields {
    id
    ...ControlBarSeriesButton_series
  }
`);

const FOOTER_CONTROL_BAR_SERIES_DETAILS_FRAGMENT = gql(`
  fragment FooterControlBar_seriesDetails on SeriesFields {
    id
    ...ControlBarSeriesButton_seriesDetails
  }
`);

interface FooterControlBarProps {
  postId: string;
  postDetails?: DocumentType<typeof FOOTER_CONTROL_BAR_POST_FRAGMENT>;
  series?: DocumentType<typeof FOOTER_CONTROL_BAR_SERIES_FRAGMENT>;
  seriesDetails?: DocumentType<
    typeof FOOTER_CONTROL_BAR_SERIES_DETAILS_FRAGMENT
  >;
}

export const FooterControlBar = ({
  postId,
  postDetails,
  series,
  seriesDetails,
}: FooterControlBarProps) => {
  const [isInsightsPopupOpen, setIsInsightsPopupOpen] = useState(false);

  return (
    <div className={styles.footerContainer}>
      <div className={styles.footerInsightsButtonWrapper}>
        {!!postDetails?.publishedAt && (
          <ControlBarInsightsButton
            open={isInsightsPopupOpen}
            onOpenChange={setIsInsightsPopupOpen}
            popOverPlacement="top-end"
            post={postDetails}
          >
            {postDetails.archivedAt ? (
              <>
                Archived <Timestamp date={postDetails.archivedAt} />
              </>
            ) : (
              <>
                Published <Timestamp date={postDetails.publishedAt} />
              </>
            )}
          </ControlBarInsightsButton>
        )}
      </div>
      {series && (
        <ControlBarSeriesButton
          postId={postId}
          series={series}
          seriesDetails={seriesDetails}
        />
      )}
    </div>
  );
};

interface ControlBarProps {
  post: DocumentType<typeof POST_FRAGMENT>;
}

const ControlBar = ({ post }: ControlBarProps) => {
  const [isBacklinksMenuOpen, setIsBacklinksMenuOpen] = useAtom(
    isBacklinksMenuOpenAtom,
  );
  const [isActivityMenuOpen, setIsActivityMenuOpen] = useAtom(
    isActivityMenuOpenAtom,
  );
  const [isInsightsMenuOpen, setIsInsightsMenuOpen] = useAtom(
    isInsightsMenuOpenAtom,
  );
  const [isAddOrShareMenuOpen, setIsAddOrShareMenuOpen] = useAtom(
    isAddOrShareMenuOpenAtom,
  );

  const readOnly = useQuillReadOnly();
  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const verificationStatus = getVerificationStatus(post);

  const { data } = useQuery(CONTROL_BAR_POST_QUERY, {
    variables: {
      postId: post.id,
      beforeAt: POST_ACTIVITIES_END_OF_TIME,
    },
  });

  const postWithBacklinks = data?.post;
  const postActivities = data?.alphaPostActivities;

  useEffect(() => {
    return () => {
      setIsBacklinksMenuOpen(false);
      setIsActivityMenuOpen(false);
      setIsInsightsMenuOpen(false);
      setIsAddOrShareMenuOpen(false);
    };
  }, [
    post.id,
    setIsActivityMenuOpen,
    setIsAddOrShareMenuOpen,
    setIsBacklinksMenuOpen,
    setIsInsightsMenuOpen,
  ]);

  useEffect(() => {
    if (isMobile && isInsightsMenuOpen) {
      setIsInsightsMenuOpen(false);
    }
  }, [isMobile, setIsInsightsMenuOpen, isInsightsMenuOpen]);

  useEffect(() => {
    if (isMobile && isBacklinksMenuOpen) {
      setIsBacklinksMenuOpen(false);
    }
  }, [isMobile, setIsBacklinksMenuOpen, isBacklinksMenuOpen]);

  const verifiedAt = post.verificationEntries[0]?.verifiedAt as
    | string
    | undefined;

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <ControlBarInsightsButton
          open={isInsightsMenuOpen}
          onOpenChange={setIsInsightsMenuOpen}
          popOverPlacement="bottom-end"
          post={post}
        >
          {post.publishedAt &&
          !post.archivedAt &&
          verificationStatus === VerificationStatus.Verified &&
          verifiedAt ? (
            <div className={styles.verifiedContainer}>
              <div className={styles.verifiedIcon}>
                <VerifiedIcon />
              </div>
              <div>
                Verified <Timestamp date={verifiedAt} />
              </div>
            </div>
          ) : (
            <>
              {post.externalPost ? (
                <>
                  Synced <Timestamp date={post.updatedAt} />
                </>
              ) : (
                <>
                  Edited <Timestamp date={post.updatedAt} />
                </>
              )}
            </>
          )}
        </ControlBarInsightsButton>
        <ControlBarVerificationButton post={post} />
        {!readOnly && (
          <ControlBarPopOver
            placement="bottom-end"
            open={isAddOrShareMenuOpen}
            onOpenChange={setIsAddOrShareMenuOpen}
            arrowClassName={styles.addOrShareArrow}
            content={
              <ControlBarAddOrSharePopOver
                onClose={() => setIsAddOrShareMenuOpen(false)}
                post={post}
              />
            }
          >
            <ControlBarButton
              active={isAddOrShareMenuOpen}
              icon={
                <ControlBarPopOver.Anchor>
                  <SVGIcon>{addOrShareIcon}</SVGIcon>
                </ControlBarPopOver.Anchor>
              }
            >
              <TutorialBeacon id={BEACON_POST_ADD_OR_SHARE} placement="top-end">
                {(beaconProps) => <div {...beaconProps}>Add or share...</div>}
              </TutorialBeacon>
            </ControlBarButton>
          </ControlBarPopOver>
        )}
      </div>

      <div className={styles.right}>
        {!isMobile &&
          postWithBacklinks &&
          postWithBacklinks.backlinks.filter(
            (backlink) => backlink.id !== postWithBacklinks.id,
          ).length > 0 && (
            <ControlBarPopOver
              content={<ControlBarBacklinks post={postWithBacklinks} />}
              open={isBacklinksMenuOpen}
              placement="bottom-start"
              onOpenChange={setIsBacklinksMenuOpen}
            >
              <ControlBarButton
                active={isBacklinksMenuOpen}
                icon={<SVGIcon>{backlinksIcon}</SVGIcon>}
              />
            </ControlBarPopOver>
          )}
        {postActivities && (
          <ControlBarActivityButton
            open={isActivityMenuOpen}
            setOpen={setIsActivityMenuOpen}
            post={post}
            activities={postActivities}
          />
        )}
      </div>
    </div>
  );
};

export default ControlBar;
